// All Potree 2 clouds share the server cooldown, rather than retrying in parallel.
let serverRetryAt = 0;
let nextRequestAt = 0;
const TIMEOUT_MS = 30000;
const MAX_ATTEMPTS = 6;
// Collect ranges across several visibility updates before opening a request.
// A short window produces larger batches on the rate-limited dev host.
const RANGE_BATCH_WINDOW_MS = 50;
// InMotion accepted 177 byte ranges in one request. Keep below Apache's usual
// 200-range default while allowing one request to cover a full visible batch.
const MAX_RANGES_PER_REQUEST = 180;
const MAX_CACHED_RANGE_BYTES = 32 * 1024 * 1024;
const rangeEntries = new Map();
const pendingRangesByUrl = new Map();
const scheduledRangeFlushes = new Set();
let cachedRangeBytes = 0;

async function waitForRequestSlot(){
	const interval = Math.max(0, Number(Potree.nodeRequestIntervalMs) || 0);
	const scheduled = Math.max(Date.now(), nextRequestAt, serverRetryAt);
	nextRequestAt = scheduled + interval;
	if(scheduled > Date.now()){
		await new Promise(resolve => setTimeout(resolve, scheduled - Date.now()));
	}
	while(Date.now() < serverRetryAt){
		await new Promise(resolve => setTimeout(resolve, serverRetryAt - Date.now()));
	}
}

export function canLoadNode(node){
	return !node.loaded && !node.loading && !node.loadFailed
		&& Date.now() >= (node.retryAt || 0) && Date.now() >= serverRetryAt;
}

export function recordLoadFailure(node, error){
	node.loadAttempts = (node.loadAttempts || 0) + 1;
	node.retryAt = Math.max(serverRetryAt, Date.now() + Math.min(30000, 1000 * 2 ** (node.loadAttempts - 1)));
	node.loadFailed = node.loadAttempts >= MAX_ATTEMPTS;
	console.warn(`Failed to load ${node.name}: ${error.message}. ${node.loadFailed
		? 'Retry limit reached; reload the viewer to retry.' : 'Will retry after cooldown.'}`);
}

function rangeKey(url, first, size){
	return `${url}|${first}|${size}`;
}

function encodeAscii(value){
	return new TextEncoder().encode(value);
}

function findBytes(buffer, needle, start){
	const limit = buffer.length - needle.length;
	for(let i = start; i <= limit; i++){
		if(buffer[i] !== needle[0]) continue;
		let matched = true;
		for(let j = 1; j < needle.length; j++){
			if(buffer[i + j] !== needle[j]){
				matched = false;
				break;
			}
		}
		if(matched) return i;
	}
	return -1;
}

function parseContentRange(value){
	const match = /^bytes\s+(\d+)-(\d+)\/(\d+|\*)$/i.exec(value || '');
	if(!match) return null;
	return {first: BigInt(match[1]), last: BigInt(match[2])};
}

function updateServerCooldown(response, attempts){
	if(response.status !== 429 && response.status !== 503) return;

	const value = response.headers.get('Retry-After');
	const seconds = value === null ? NaN : Number(value);
	const requested = Number.isFinite(seconds) ? Date.now() + Math.max(0, seconds) * 1000 : Date.parse(value);
	const backoff = Date.now() + Math.max(5000, Math.min(30000, 1000 * 2 ** attempts));
	serverRetryAt = Math.max(serverRetryAt, backoff, Number.isFinite(requested) ? requested : 0);
}

function resourceTooLargeError(maxBytes){
	const error = new Error(`Resource exceeds the ${maxBytes}-byte prefetch limit`);
	error.code = 'RESOURCE_TOO_LARGE';
	return error;
}

async function readResponseBody(response, maxBytes){
	const contentLength = Number(response.headers.get('Content-Length'));
	if(Number.isFinite(contentLength) && contentLength > maxBytes){
		throw resourceTooLargeError(maxBytes);
	}

	const reader = response.body && response.body.getReader ? response.body.getReader() : null;
	if(!reader){
		const buffer = await response.arrayBuffer();
		if(buffer.byteLength > maxBytes) throw resourceTooLargeError(maxBytes);
		return buffer;
	}

	const chunks = [];
	let length = 0;
	try{
		while(true){
			const {done, value} = await reader.read();
			if(done) break;
			length += value.byteLength;
			if(length > maxBytes){
				await reader.cancel();
				throw resourceTooLargeError(maxBytes);
			}
			chunks.push(value);
		}
	}catch(error){
		try{ await reader.cancel(); }catch{}
		throw error;
	}

	const result = new Uint8Array(length);
	let offset = 0;
	for(const chunk of chunks){
		result.set(chunk, offset);
		offset += chunk.byteLength;
	}
	return result.buffer;
}

export async function fetchWholeResource(url, maxBytes, attempts = 0){
	maxBytes = Number.isFinite(maxBytes) && maxBytes >= 0 ? maxBytes : Number.MAX_SAFE_INTEGER;
	let controller;
	let timeout;
	try{
		await waitForRequestSlot();
		controller = new AbortController();
		timeout = setTimeout(() => controller.abort(), TIMEOUT_MS);
		const response = await fetch(url, {signal: controller.signal});
		updateServerCooldown(response, attempts);
		if(response.status !== 200){
			controller.abort();
			throw new Error(`HTTP ${response.status} fetching ${url}; expected a complete resource`);
		}
		let buffer;
		try{
			buffer = await readResponseBody(response, maxBytes);
		}catch(error){
			if(error.code === 'RESOURCE_TOO_LARGE') controller.abort();
			throw error;
		}
		if(buffer.byteLength === 0) throw new Error(`Empty resource fetching ${url}`);
		return buffer;
	}finally{
		if(timeout) clearTimeout(timeout);
	}
}

function parseMultipartRanges(buffer, contentType){
	const boundaryMatch = /(?:^|;)\s*boundary=(?:"([^"]+)"|([^;\s]+))/i.exec(contentType || '');
	const boundary = boundaryMatch && (boundaryMatch[1] || boundaryMatch[2]);
	if(!boundary) throw new Error('Multipart range response is missing its boundary');

	const bytes = new Uint8Array(buffer);
	const marker = encodeAscii(`--${boundary}`);
	const nextPartMarker = encodeAscii(`\r\n--${boundary}`);
	const headerSeparator = encodeAscii('\r\n\r\n');
	const decoder = new TextDecoder('ascii');
	const parts = [];
	let cursor = findBytes(bytes, marker, 0);

	while(cursor !== -1){
		let headerStart = cursor + marker.length;
		if(bytes[headerStart] === 45 && bytes[headerStart + 1] === 45) break;
		if(bytes[headerStart] === 13 && bytes[headerStart + 1] === 10) headerStart += 2;

		const headerEnd = findBytes(bytes, headerSeparator, headerStart);
		if(headerEnd === -1) throw new Error('Invalid multipart range headers');
		const headers = decoder.decode(bytes.subarray(headerStart, headerEnd));
		const contentRangeLine = headers.split('\r\n').find(line => /^content-range\s*:/i.test(line));
		const range = parseContentRange(contentRangeLine && contentRangeLine.slice(contentRangeLine.indexOf(':') + 1).trim());
		if(!range) throw new Error('Multipart range part is missing a valid Content-Range');

		const bodyStart = headerEnd + headerSeparator.length;
		const nextBoundary = findBytes(bytes, nextPartMarker, bodyStart);
		if(nextBoundary === -1) throw new Error('Multipart range response ended before its closing boundary');
		parts.push({first: range.first, last: range.last, bytes: bytes.subarray(bodyStart, nextBoundary)});
		cursor = nextBoundary + 2;
	}

	if(parts.length === 0) throw new Error('Multipart range response contains no parts');
	return parts;
}

function getResponseParts(buffer, response){
	const contentType = response.headers.get('Content-Type') || '';
	if(/^multipart\/byteranges\b/i.test(contentType)){
		return parseMultipartRanges(buffer, contentType);
	}

	const range = parseContentRange(response.headers.get('Content-Range'));
	if(!range) throw new Error('Range response is missing a valid Content-Range');
	return [{first: range.first, last: range.last, bytes: new Uint8Array(buffer)}];
}

function sliceRequestedRanges(parts, entries){
	return entries.map(entry => {
		const last = entry.first + entry.size - 1n;
		const part = parts.find(candidate => candidate.first <= entry.first && candidate.last >= last);
		if(!part) throw new Error(`Range response did not contain bytes ${entry.first}-${last}`);

		const start = Number(entry.first - part.first);
		const end = start + Number(entry.size);
		if(start < 0 || end > part.bytes.byteLength){
			throw new Error(`Range response was truncated for bytes ${entry.first}-${last}`);
		}

		return part.bytes.slice(start, end).buffer;
	});
}

function settleRangeEntry(entry, result){
	if(entry.state === 'ready' && entry.buffer) cachedRangeBytes -= entry.buffer.byteLength;
	entry.state = result.error ? 'failed' : 'ready';
	entry.buffer = result.buffer || null;
	entry.error = result.error || null;
	entry.retryAt = result.retryAt || 0;
	if(entry.buffer){
		cachedRangeBytes += entry.buffer.byteLength;
	}
	entry.resolve({buffer: entry.buffer, error: entry.error});
	trimRangeCache();
}

function trimRangeCache(){
	if(cachedRangeBytes <= MAX_CACHED_RANGE_BYTES) return;
	const ready = [...rangeEntries.values()]
		.filter(entry => entry.state === 'ready')
		.sort((a, b) => a.lastUsedAt - b.lastUsedAt);
	for(const entry of ready){
		if(cachedRangeBytes <= MAX_CACHED_RANGE_BYTES) break;
		if(rangeEntries.get(entry.key) !== entry) continue;
		rangeEntries.delete(entry.key);
		cachedRangeBytes -= entry.buffer.byteLength;
		entry.buffer = null;
	}
}

function createRangeEntry(url, first, size, attempts){
	const key = rangeKey(url, first, size);
	const entry = {key, url, first, size, attempts, state: 'queued', buffer: null, error: null, retryAt: 0, lastUsedAt: Date.now()};
	entry.promise = new Promise(resolve => { entry.resolve = resolve; });
	rangeEntries.set(key, entry);
	let queue = pendingRangesByUrl.get(url);
	if(!queue){
		queue = [];
		pendingRangesByUrl.set(url, queue);
	}
	queue.push(entry);
	scheduleRangeFlush(url);
	return entry;
}

function queueRange(url, first, size, attempts = 0){
	const key = rangeKey(url, first, size);
	let entry = rangeEntries.get(key);
	if(entry && entry.state === 'failed' && Date.now() >= entry.retryAt){
		rangeEntries.delete(key);
		entry = null;
	}
	return entry || createRangeEntry(url, first, size, attempts);
}

function scheduleRangeFlush(url){
	if(scheduledRangeFlushes.has(url)) return;
	scheduledRangeFlushes.add(url);
	const interval = Math.max(0, Number(Potree.nodeRequestIntervalMs) || 0);
	const batchWindow = interval > 0 ? RANGE_BATCH_WINDOW_MS : 0;
	setTimeout(() => flushRangeQueue(url), batchWindow);
}

function lowerRangeLimit(url, attemptedCount){
	const current = rangeLimitsByUrl.get(url) || MAX_RANGES_PER_REQUEST;
	if(current <= 1) return current;
	const reduced = Math.max(1, Math.min(current - 1, Math.floor(attemptedCount / 2)));
	rangeLimitsByUrl.set(url, reduced);
	return reduced;
}

const rangeLimitsByUrl = new Map();

function requeueRangeBatch(url, entries, nextLimit){
	for(const entry of entries) entry.state = 'queued';
	let queue = pendingRangesByUrl.get(url) || [];
	queue = entries.concat(queue);
	pendingRangesByUrl.set(url, queue);
	if(nextLimit > 0) rangeLimitsByUrl.set(url, nextLimit);
	scheduleRangeFlush(url);
}

function failRangeBatch(entries, error){
	const retryAt = Math.max(serverRetryAt, Date.now() + 1000);
	for(const entry of entries){
		if(rangeEntries.get(entry.key) === entry){
			settleRangeEntry(entry, {error, retryAt});
		}
	}
}

async function flushRangeQueue(url){
	scheduledRangeFlushes.delete(url);
	const queue = pendingRangesByUrl.get(url);
	if(!queue || queue.length === 0) return;

	const rangeLimit = rangeLimitsByUrl.get(url) || MAX_RANGES_PER_REQUEST;
	const entries = queue.splice(0, rangeLimit);
	if(queue.length === 0) pendingRangesByUrl.delete(url);

	entries.sort((a, b) => a.first < b.first ? -1 : a.first > b.first ? 1 : 0);
	for(const entry of entries) entry.state = 'requesting';

	let controller;
	let timeout;
	try{
		await waitForRequestSlot();
		controller = new AbortController();
		timeout = setTimeout(() => controller.abort(), TIMEOUT_MS);
		const rangeHeader = entries.map(entry => `${entry.first}-${entry.first + entry.size - 1n}`).join(',');
		const response = await fetch(url, {
			headers: {Range: `bytes=${rangeHeader}`},
			signal: controller.signal,
		});

		updateServerCooldown(response, Math.max(...entries.map(entry => entry.attempts)));

		if(response.status !== 206){
			controller.abort();
			if(entries.length > 1 && [200, 400, 416, 431].includes(response.status)){
				const nextLimit = lowerRangeLimit(url, entries.length);
				requeueRangeBatch(url, entries, nextLimit);
				return;
			}
			throw new Error(`HTTP ${response.status} fetching ${url}; expected a byte-range response`);
		}

		const body = await response.arrayBuffer();
		const parts = getResponseParts(body, response);
		const buffers = sliceRequestedRanges(parts, entries);
		for(let i = 0; i < entries.length; i++){
			const entry = entries[i];
			if(rangeEntries.get(entry.key) === entry){
				settleRangeEntry(entry, {buffer: buffers[i]});
			}
		}
	}catch(error){
		failRangeBatch(entries, error);
	}finally{
		if(timeout) clearTimeout(timeout);
	}

	if(pendingRangesByUrl.has(url)) scheduleRangeFlush(url);
}

export function prefetchNodeRanges(url, ranges){
	for(const range of ranges){
		if(!range || range.size <= 0n) continue;
		const entry = queueRange(url, range.first, range.size, range.attempts || 0);
		entry.lastUsedAt = Date.now();
	}
}

export async function fetchNodeRange(url, first, size, attempts = 0){
	if(size <= 0n) return new ArrayBuffer(0);
	const entry = queueRange(url, first, size, attempts);
	entry.lastUsedAt = Date.now();
	const result = await entry.promise;
	if(result.error){
		if(rangeEntries.get(entry.key) === entry) rangeEntries.delete(entry.key);
		throw result.error;
	}
	if(rangeEntries.get(entry.key) === entry){
		rangeEntries.delete(entry.key);
		if(entry.buffer) cachedRangeBytes -= entry.buffer.byteLength;
	}
	return result.buffer.slice(0);
}

export function decodeNode(workerPath, message){
	return new Promise((resolve, reject) => {
		const worker = Potree.workerPool.getWorker(workerPath);
		let settled = false;
		const finish = (error, data) => {
			if(settled) return;
			settled = true;
			clearTimeout(timeout);
			worker.onmessage = worker.onerror = worker.onmessageerror = null;
			if(error){
				worker.terminate();
				reject(error);
			}else{
				Potree.workerPool.returnWorker(workerPath, worker);
				resolve(data);
			}
		};
		const timeout = setTimeout(() => finish(new Error('Point decoder timed out')), TIMEOUT_MS);
		worker.onmessage = event => {
			if(!event.data || !event.data.attributeBuffers){
				finish(new Error('Invalid point decoder response'));
			}else{
				finish(null, event.data);
			}
		};
		worker.onerror = event => {
			event.preventDefault();
			finish(new Error(event.message || 'Point decoder failed'));
		};
		worker.onmessageerror = () => finish(new Error('Unable to receive point decoder result'));
		try{
			worker.postMessage(message, [message.buffer]);
		}catch(error){
			finish(error);
		}
	});
}
